import React, { useContext, useState, useEffect } from 'react';
import { Download, Filter, X, Search, Eye, Edit, Trash2, Plus, Check, XCircle, ChevronDown, ArrowLeft, History, FileSpreadsheet } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import riskService from '../../../../services/riskService';
import userService from '../../../../services/userService';
import exportService from '../../../../services/exportService';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import ExportRiskFilterModal from './ExportRiskFilterModal';
import Pagination from '../../../../components/Pagination';

export default function AllOrganisationRisks() {
  const { logoutUser,hasRole, user, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    risk_name: '',
    probability: '',
    status: '',
    assigned_user: '',
    ordering: 'newest'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    riskId: null,
    riskName: '',
    action: '',
    loading: false
  });

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    risk: null,
    isLoading: false
  });

  // Export modal state - simplified
  const [exportModal, setExportModal] = useState({
    isOpen: false,
    type: '',
    loading: false,
    filters: {
      risk_name: '',
      assigned_user: '',
      probability: '',
      status: '',
      created_date_from: '',
      created_date_to: '',
      ordering: 'newest'
    }
  });

  useEffect(() => {
    fetchOrganizationRisks();
  }, [pagination.page, pagination.page_size]);

  const fetchOrganizationRisks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      const result = await riskService.getOrganizationRisks(authTokens.access, filters, pagination_data);
      
      if (result.success) {
        setRisks(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement des risques');
      }
    } catch (error) {
      console.error('Error fetching organization risks:', error);
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };



  const canAddRisk = () => hasRole("RiskManager") || hasRole("RiskIdentifier");
  const hasEditPermissions = () => hasRole("RiskManager") || hasRole("RiskIdentifier");
  const canEditRisk = (risk) => hasEditPermissions() && risk.status.toLowerCase() !== "approved";
  const canDeleteRisk = () => hasRole("Admin");
  const canViewHistory = () => hasRole("Admin");
  const canViewAllRisks = () => hasRole("Admin") || hasRole("RiskManager");
  const canValidateRisk = () => hasRole("RiskManager");

  const probabiliteOptions = ['mineur', 'normal', 'majeur', 'critique'];

const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      risk_name: '',
      probability: '',
      status: '',
      assigned_user: '',
      ordering: 'newest'
    });
    setUserSearchResults([]);
    setShowUserDropdown(false);

    setPagination(prev => ({ ...prev, page: 1 }));
    // Fetch will be triggered by useEffect when filters change
    setTimeout(() => fetchOrganizationRisks(), 0);
  };

  const applyFilters = () => {
    setShowUserDropdown(false);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrganizationRisks();
  };

  const openExportModal = (type) => {
    setExportModal({
      isOpen: true,
      type: type,
      loading: false,
      filters: {
        risk_name: '',
        assigned_user: '',
        probability: '',
        status: '',
        created_date_from: '',
        created_date_to: '',
        ordering: 'newest'
      }
    });
  };

  const closeExportModal = () => {
    setExportModal({
      isOpen: false,
      type: '',
      loading: false,
      filters: {
        risk_name: '',
        assigned_user: '',
        probability: '',
        status: '',
        created_date_from: '',
        created_date_to: '',
        ordering: 'newest'
      }
    });
  };

  const handleExportFilterChange = (field, value) => {
    setExportModal({
      ...exportModal,
      filters: {
        ...exportModal.filters,
        [field]: value
      }
    });
  };

  const handleExport = async () => {
    setExportModal({ ...exportModal, loading: true });
    
    try {
      let result;
      if (exportModal.type === 'pdf') {
        result = await exportService.exportRisksPDF(authTokens.access, exportModal.filters);
      } else {
        result = await exportService.exportRisksExcel(authTokens.access, exportModal.filters);
      }
      
      if (result.success) {
        showSuccess(result.message || `Export ${exportModal.type.toUpperCase()} réussi`);
        closeExportModal();
      } else {
        showError(result.error || `Erreur lors de l'export ${exportModal.type.toUpperCase()}`);
      }
    } catch (error) {
      showError(`Erreur lors de l'export ${exportModal.type.toUpperCase()}`);
    } finally {
      setExportModal({ ...exportModal, loading: false });
    }
  };

  const handleShowFiltersSection = () => {
    setShowFiltersSection(!showFiltersSection);
    if (showFiltersSection) {
      setShowUserDropdown(false);
    }
  };

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    handleFilterChange('assigned_user', query);
    
    if (query.length >= 2) {
      setUserSearchLoading(true);
      try {
        const result = await userService.filterUsersBySearch(query, authTokens.access);
        if (result.success) {
          setUserSearchResults(result.data || []);
          setShowUserDropdown(true);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    } else {
      setUserSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const selectUser = (user) => {
    handleFilterChange('assigned_user', user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  const handleEditRisk = (risk) => {
    navigate(`/dashboard/risks/edit/${risk.id}`, { state: { risk } });
  };

  const handleViewRisk = (risk) => {
    navigate(`/dashboard/risks/${risk.id}`);
  };

  const handleViewHistory = (risk) => {
    navigate(`/dashboard/admin/detailedLogs/risk/${risk.id}`);
  };

  const handleDeleteRisk = (risk) => {
    setDeleteModal({
      isOpen: true,
      risk: risk,
      isLoading: false
    });
  };

  const confirmDeleteRisk = async () => {
    if (!deleteModal.risk) return;

    setDeleteModal(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await riskService.deleteRisk(deleteModal.risk.id, authTokens.access);
      
      if (result.success) {
        showSuccess('Risque supprimé avec succès');
        fetchOrganizationRisks();
        setDeleteModal({ isOpen: false, risk: null, isLoading: false });
      } else {
        showError(result.error || 'Erreur lors de la suppression');
        setDeleteModal(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error deleting risk:', error);
      showError('Erreur de connexion');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const cancelDeleteRisk = () => {
    setDeleteModal({ isOpen: false, risk: null, isLoading: false });
  };

  const openConfirmationModal = (riskId, riskName, action) => {
    setConfirmationModal({
      isOpen: true,
      riskId,
      riskName,
      action,
      loading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      riskId: null,
      riskName: '',
      action: '',
      loading: false
    });
  };

  const handleConfirmValidation = async () => {
    setConfirmationModal(prev => ({ ...prev, loading: true }));
    
    try {
      const result = await riskService.validateRisk(
        confirmationModal.riskId, 
        confirmationModal.action, 
        authTokens.access
      );
      
      if (result.success) {
        showSuccess(result.data.message || `Risque ${confirmationModal.action === 'approved' ? 'approuvé' : 'rejeté'} avec succès`);
        fetchOrganizationRisks();
        closeConfirmationModal();
      } else {
        showError(result.error || 'Erreur lors de la validation');
        setConfirmationModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error validating risk:', error);
      showError('Erreur de connexion');
      setConfirmationModal(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.relative')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const getProbabiliteBadge = (probabilite) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (probabilite?.toLowerCase()) {
      case 'critique':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'majeur':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'normal':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'mineur':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status?.toLowerCase()) {
      case 'validé':
      case 'approved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'rejeté':
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'en-cours':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'pending':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!canViewAllRisks()) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-yellow-800">
          <h3 className="font-medium">Accès restreint</h3>
          <p className="text-sm mt-1">
            Désolé, vous n'avez pas les autorisations pour afficher cette page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center space-x-3">
          <Link 
            to={-1}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h2 className="text-xl font-semibold text-gray-900">Tous les Risques de l'Organisation</h2>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleShowFiltersSection}
            className="flex items-center px-3 py-1.5 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <Filter className="h-3 w-3 mr-1.5" />
            Filtres
          </button>

          <button
            onClick={() => openExportModal('pdf')}
            className="flex items-center px-3 py-1.5 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            <Download className="h-3 w-3 mr-1.5" />
            Export PDF
          </button>
          <button
            onClick={() => openExportModal('excel')}
            className="flex items-center px-3 py-1.5 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <FileSpreadsheet className="h-3 w-3 mr-1.5" />
            Export Excel
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={fetchOrganizationRisks}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className='flex items-center justify-between mb-3'>
            <div className="flex items-center">
              <Filter className="h-4 w-4 text-gray-500 mr-2" />
              <h3 className="text-base font-medium text-gray-900">Filtres</h3>
            </div>
            <button onClick={handleShowFiltersSection}>
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            {/* Name Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom du risque
              </label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  value={filters.risk_name}
                  onChange={(e) => handleFilterChange('risk_name', e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Probabilité Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Probabilité
              </label>
              <select
                value={filters.probability}
                onChange={(e) => handleFilterChange('probability', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Toutes</option>
                {probabiliteOptions.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
              </select>
            </div>

            {/* Assigned User Filter */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Utilisateur assigné
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.assigned_user}
                  onChange={handleUserSearch}
                  onFocus={() => filters.assigned_user.length >= 2 && setShowUserDropdown(true)}
                  placeholder="Rechercher utilisateur..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {userSearchLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Recherche...</div>
                    ) : userSearchResults.length > 0 ? (
                      userSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => selectUser(user)}
                          className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{user.email}</div>
                          <div className="text-xs text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        </div>
                      ))
                    ) : filters.assigned_user ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Aucun utilisateur trouvé</div>
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Tapez pour rechercher...</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Ordering Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Ordre
              </label>
              <select
                value={filters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="newest">Plus récents</option>
                <option value="oldest">Plus anciens</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex space-x-2">
            <button
              onClick={applyFilters}
              className="flex items-center px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
            >
              <Filter className="h-3 w-3 mr-1.5" />
              Appliquer
            </button>
            <button
              onClick={clearFilters}
              className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              <X className="h-3 w-3 mr-1.5" />
              Effacer
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Probabilité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé par
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {risks.map((risk) => (
                <tr key={risk.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {risk.risk_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getProbabiliteBadge(risk.probability)}>
                      {risk.probability}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(risk.risk_date).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(risk.status)}>
                      {risk.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {risk.created_by ? 
                      <div>
                        <p className='font-semibold text-black'>{risk.created_by.first_name || 'N/A'} {risk.created_by.last_name || 'N/A'}</p>
                        <p>{risk.created_by.email || 'N/A'}</p>
                      </div>
                      : "N/A"
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewRisk(risk)}
                        className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      {canEditRisk(risk) && (
                        <button 
                          onClick={() => handleEditRisk(risk)}
                          className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}

                      {/* {canDeleteRisk() && (
                        <button 
                          onClick={() => handleDeleteRisk(risk)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button> 
                      )} */}

                      {canViewHistory() && (
                        <button 
                          onClick={() => handleViewHistory(risk)}
                          className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded"
                          title="Afficher l'historique"
                        >
                          <History className="h-4 w-4" />
                        </button> 
                      )}
                      
                      {canValidateRisk() && risk.status?.toLowerCase() === 'pending' && (
                        <>
                          <button 
                            onClick={() => openConfirmationModal(risk.id, risk.risk_name, 'approved')}
                            className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                            title="Approuver le risque"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => openConfirmationModal(risk.id, risk.risk_name, 'rejected')}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                            title="Rejeter le risque"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="Risks" 
        />

        {/* Empty State */}
        {risks.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <p className="text-lg font-medium">Aucun risque trouvé</p>
              <p className="mt-1">Aucun risque n'a été créé dans votre organisation.</p>
              {canAddRisk() && (
                <Link
                  to="/dashboard/risks/add"
                  className="inline-flex items-center mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer le premier risque
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Validation Confirmation Modal */}
      {confirmationModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-full mr-3 ${
                confirmationModal.action === 'approved' 
                  ? 'bg-green-100' 
                  : 'bg-red-100'
              }`}>
                {confirmationModal.action === 'approved' ? (
                  <Check className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {confirmationModal.action === 'approved' ? 'Approuver le risque' : 'Rejeter le risque'}
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir{' '}
              <span className={`font-semibold ${
                confirmationModal.action === 'approved' ? 'text-green-600' : 'text-red-600'
              }`}>
                {confirmationModal.action === 'approved' ? 'approuver' : 'rejeter'}
              </span>{' '}
              le risque "{confirmationModal.riskName}" ?
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeConfirmationModal}
                disabled={confirmationModal.loading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmValidation}
                disabled={confirmationModal.loading}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center ${
                  confirmationModal.action === 'approved'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmationModal.loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {confirmationModal.action === 'approved' ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDeleteRisk}
        onConfirm={confirmDeleteRisk}
        title="Supprimer le risque"
        message="Êtes-vous sûr de vouloir supprimer ce risque ? Cette action est irréversible."
        itemName={deleteModal.risk ? deleteModal.risk.risk_name : ''}
        isLoading={deleteModal.isLoading}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      {/* Export Modal - Using the separate component */}
      <ExportRiskFilterModal
        isOpen={exportModal.isOpen}
        onClose={closeExportModal}
        onExport={handleExport}
        exportType={exportModal.type}
        loading={exportModal.loading}
        filters={exportModal.filters}
        onFilterChange={handleExportFilterChange}
        authTokens={authTokens}
      />
    </div>
  );
}